import React from 'react'
import { useBoardContext } from '../../../hooks/use-board'
import {
    DndContext,
    DragEndEvent,
    DragOverEvent,
    DragOverlay,
    DragStartEvent,
    PointerSensor,
    rectIntersection,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import { arrayMove, horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable'
import { generateKeyBetween } from 'fractional-indexing'
import { Flex } from 'antd'
import { AddColumnColumn } from './add-column-column'
import { DragData } from './board'
import { DragOverlayCandidate } from './drag-overlay-candidate'
import { DragOverlayColumn } from './drag-overlay-column'
import { MemoizedSortableColumn } from './memoized-sortable-column'
import { useReorderCampaignColumn } from '../../hooks'
import { useUpdateCandidateColumn, useUpdateCandidateCompanyColumn } from '../../../candidate/hooks'
import { useReorderCompanyColumn } from '../../../company/hooks'
import { useAuth } from '../../../auth'

export function BoardContent({
    style,
    height,
}: {
    style?: React.CSSProperties
    height?: React.CSSProperties['maxHeight']
}) {
    const {
        columns,
        columnOrder,
        setColumnOrder,
        activeItem,
        setActiveItem,
        campaignId,
        activeColumn,
        setActiveColumn,
        setOverId,
        overId,
        dropLinePosition,
        setDropLinePosition,
        isColumnOver,
        columnCandidates,
        moveCandidateOptimistically,
        setHasPendingOptimisticUpdate,
    } = useBoardContext()

    const { activeCompany } = useAuth()
    const updateCandidateColumn = useUpdateCandidateColumn()
    const updateCandidateCompanyColumn = useUpdateCandidateCompanyColumn()
    const reorderCampaignColumns = useReorderCampaignColumn(campaignId || '')
    const reorderCompanyColumns = useReorderCompanyColumn(activeCompany?.id || '')

    // Select the appropriate mutation based on mode
    const updateMutation = campaignId ? updateCandidateColumn : updateCandidateCompanyColumn

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }))

    const handleDragStart = (event: DragStartEvent) => {
        const { active } = event
        const activeData = active.data.current

        if (activeData?.type === 'column') {
            setActiveColumn(activeData as { type: 'column'; columnId: string })
            setActiveItem(null)
        } else {
            setActiveItem(activeData as DragData)
            setActiveColumn(null)
        }
    }

    const handleDragOver = (event: DragOverEvent) => {
        const { over, active } = event
        const newOverId = over?.id ?? null

        if (overId === newOverId) {
            return
        }

        setOverId(newOverId)

        // Handle column sorting
        if (activeColumn) {
            return
        }

        if (!over || !active || !activeItem) {
            setDropLinePosition(null)
            return
        }

        const activeData = activeItem
        let targetColumnId: string | null = null
        let targetIndex = 0

        // Check if dropping over a column container
        if (typeof newOverId === 'string' && newOverId.startsWith('column-')) {
            targetColumnId = newOverId.replace('column-', '')
            // Filter out the active item to match the rendered list
            const candidates = (columnCandidates[targetColumnId] || []).filter((c) => c.id !== activeData.item.id)
            targetIndex = candidates.length // Drop at end of column
        } else if (typeof newOverId === 'string') {
            // Dropping over a specific candidate
            // Find which column this candidate belongs to
            for (const colId of columnOrder) {
                // Filter out the active item to match the rendered list
                const candidates = (columnCandidates[colId] || []).filter((c) => c.id !== activeData.item.id)
                const candidateIndex = candidates.findIndex((c) => c.id === newOverId)

                if (candidateIndex !== -1) {
                    targetColumnId = colId
                    // Since we filtered out the active item, the index is already correct
                    targetIndex = candidateIndex
                    break
                }
            }
        }

        if (!targetColumnId) {
            setDropLinePosition(null)
            return
        }

        setDropLinePosition({
            columnId: targetColumnId,
            index: targetIndex,
        })
    }

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event
        const activeId = active.id
        const overId = over?.id

        const clear = () => {
            setActiveItem(null)
            setActiveColumn(null)
            setOverId(null)
            setDropLinePosition(null)
        }

        if (activeId === overId) {
            clear()
            return
        }

        const isColumnDrag =
            activeColumn &&
            typeof activeId === 'string' &&
            typeof overId === 'string' &&
            activeId.startsWith('column-') &&
            overId.startsWith('column-')

        if (isColumnDrag) {
            const from = activeId.replace('column-', '')
            const to = overId.replace('column-', '')
            const oldIndex = columnOrder.indexOf(from)
            const newIndex = columnOrder.indexOf(to)

            if (oldIndex !== -1 && newIndex !== -1) {
                const order = arrayMove(columnOrder, oldIndex, newIndex)

                if (campaignId) {
                    reorderCampaignColumns.mutate({ order })
                } else {
                    reorderCompanyColumns.mutate(order)
                }

                setColumnOrder(order)
                setTimeout(() => {
                    clear()
                }, 250)
            }

            return
        }

        if (!activeItem || !dropLinePosition) {
            clear()
            return
        }

        const targetColumnId = dropLinePosition.columnId
        const targetIndex = dropLinePosition.index

        // Get the candidates in the target column, ALWAYS excluding the dragged candidate
        // This must match the filtering in handleDragOver to get correct indices
        const targetColumnCandidates = (columnCandidates[targetColumnId] || []).filter(
            (c) => c.id !== activeItem.item.id
        )

        const column = campaignId ? 'columnOrder' : 'companyColumnOrder'

        // Calculate the fractional index based on neighbors (now without the dragged candidate)
        let newColumnOrder: string
        if (targetColumnCandidates.length === 0) {
            // Empty column - use default fractional index
            newColumnOrder = generateKeyBetween(null, null)
        } else if (targetIndex === 0) {
            // Dropping at the top
            const nextOrder = targetColumnCandidates[0][column]
            newColumnOrder = generateKeyBetween(null, nextOrder)
        } else if (targetIndex >= targetColumnCandidates.length) {
            // Dropping at the bottom
            const prevOrder = targetColumnCandidates[targetColumnCandidates.length - 1][column]
            newColumnOrder = generateKeyBetween(prevOrder, null)
        } else {
            // Dropping in the middle
            const prevOrder = targetColumnCandidates[targetIndex - 1][column]
            const nextOrder = targetColumnCandidates[targetIndex][column]

            // Safety check: if prev and next are the same, don't move (invalid bounds for fractional indexing)
            if (prevOrder === nextOrder) {
                clear()
                return
            }

            newColumnOrder = generateKeyBetween(prevOrder, nextOrder)
        }

        // Mark that we have a pending optimistic update
        setHasPendingOptimisticUpdate(true)

        // Apply optimistic update immediately - directly update the state
        moveCandidateOptimistically(activeItem.item.id, activeItem.fromColumn, targetColumnId, newColumnOrder)

        clear()

        // Call the mutation - query refetch will sync real data
        updateMutation.mutate(
            {
                fromColumnId: activeItem.fromColumn,
                columnId: targetColumnId,
                candidateId: activeItem.item.id,
                columnOrder: newColumnOrder,
            },
            {
                onSettled: () => {
                    // Clear the pending flag so query data can sync
                    setHasPendingOptimisticUpdate(false)
                },
            }
        )
    }

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={rectIntersection}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
        >
            <Flex
                gap="large"
                align="start"
                style={{ overflowX: 'auto', maxWidth: 1000, minHeight: '60vh', ...style }}
            >
                <SortableContext
                    items={columnOrder.map((columnId) => `column-${columnId}`)}
                    strategy={horizontalListSortingStrategy}
                >
                    {columnOrder.map((columnId) => (
                        <MemoizedSortableColumn
                            key={columnId}
                            id={columnId}
                            title={columns[columnId].column.name}
                            isOver={isColumnOver(columnId)}
                            dropLinePosition={dropLinePosition}
                            height={height}
                        />
                    ))}
                </SortableContext>
                {campaignId ? (
                    <AddColumnColumn campaignId={campaignId} />
                ) : (
                    <AddColumnColumn companyId={activeCompany?.id} />
                )}
            </Flex>
            <DragOverlay>
                {activeItem && <DragOverlayCandidate candidate={activeItem.item} />}
                {activeColumn && <DragOverlayColumn />}
            </DragOverlay>
        </DndContext>
    )
}
